import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, KeyRound, Loader2, Lock, ShieldCheck, UserPlus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { clearEmailVerificationResume, readEmailVerificationResume } from '../utils/emailVerification';
import { buildAvatarLaunchUrl } from '../utils/avatarProfiles';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface AvatarSharePageProps { isDarkMode: boolean; }

interface Invitation {
  status: string;
  customerSlug: string;
  customerName: string;
  workspaceSlug: string;
  agentSlug: string;
  agentName: string;
  recipientEmail?: string | null;
  expiresAt?: string | null;
  usesRemaining?: number | null;
  targetUrl: string;
}

interface EmailVerification {
  challengeId: string;
  status: 'pending' | 'verified' | 'expired' | 'consumed' | string;
  email: string;
  expiresAt?: string;
}

export const buildAvatarShareLiveTarget = (
  customerSlug?: string | null,
  agentSlug?: string | null,
): string => buildAvatarLaunchUrl(
  String(customerSlug || 'synaworks'),
  { agent_slug: String(agentSlug || 'sally') },
);

const persistAuth = (auth: any) => {
  if (!auth?.token) throw new Error('Die Anmeldung hat kein gültiges Zugriffstoken geliefert.');
  const agentAccess = Array.isArray(auth.agent_access) ? auth.agent_access : [];
  const session = {
    username: auth.username || auth.user?.username || null,
    user_id: auth.user?.id || auth.user_id || null,
    role: auth.role || null,
    platform_role: auth.platform_role || null,
    is_superadmin: auth.role === 'superadmin' || auth.platform_role === 'superadmin' || auth.platform_role === 'owner',
    has_active_customeradmin: Boolean(auth.has_active_customeradmin),
    active_customer_id: auth.active_customer_id || auth.customer?.id || null,
    active_customer_slug: auth.active_customer_slug || auth.customer?.slug || agentAccess[0]?.customer_slug || null,
    active_customer_name: auth.active_customer_name || auth.customer?.name || null,
    active_workspace_slug: auth.active_workspace_slug || auth.workspace?.slug || agentAccess[0]?.workspace_slug || null,
    active_workspace_name: auth.active_workspace_name || auth.workspace?.name || null,
    active_agent_slug: auth.active_agent_slug || auth.agent?.slug || agentAccess[0]?.agent_slug || null,
    active_agent_name: auth.active_agent_name || auth.agent?.name || null,
    customer_memberships: auth.customer_memberships || [],
    agent_access: agentAccess,
  };
  window.sessionStorage.setItem('admin_token', auth.token);
  window.sessionStorage.setItem('admin_username', session.username || '');
  window.sessionStorage.setItem('admin_session', JSON.stringify(session));
};

const getInvitationError = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'expired': return 'Diese Einladung ist abgelaufen.';
    case 'revoked': return 'Diese Einladung wurde vom Absender widerrufen.';
    case 'exhausted': return 'Die maximale Anzahl an Nutzungen wurde erreicht.';
    case 'invalid': return 'Dieser Einladungslink ist ungültig.';
    default: return 'Diese Einladung kann nicht mehr verwendet werden.';
  }
};

const getInvitationResponseError = (httpStatus: number, payload: any): string => {
  if (httpStatus === 404) return getInvitationError('invalid');
  if (httpStatus === 410) return getInvitationError(String(payload?.status || 'expired'));
  if (httpStatus >= 500) return 'Die Einladung kann gerade nicht geprüft werden. Bitte versuchen Sie es später erneut.';
  return 'Diese Einladung kann nicht verwendet werden.';
};

const AvatarSharePage: React.FC<AvatarSharePageProps> = ({ isDarkMode }) => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [emailVerification, setEmailVerification] = useState<EmailVerification | null>(null);
  const registrationStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const loadInvitation = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const response = await fetch(`${API_BASE}/api/avatar-shares/${encodeURIComponent(token)}`);
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.invitation) {
          throw new Error(getInvitationResponseError(response.status, payload));
        }
        if (cancelled) return;
        const nextInvitation = payload.invitation as Invitation;
        setInvitation(nextInvitation);
        const resume = readEmailVerificationResume();
        const resumedEmail = resume?.status === 'verified'
          && resume.customerSlug === nextInvitation.customerSlug
          ? resume.email
          : '';
        const resumeMatchesRecipient = Boolean(
          resumedEmail
          && (
            !nextInvitation.recipientEmail
            || nextInvitation.recipientEmail.trim().toLowerCase() === resumedEmail.trim().toLowerCase()
          )
        );
        const nextEmail = nextInvitation.recipientEmail || resumedEmail;
        if (nextEmail) {
          setEmail(nextEmail);
          setIdentifier(nextEmail);
        }
        if (resume?.status === 'verified' && resumeMatchesRecipient) {
          setEmailVerification({
            challengeId: resume.challengeId,
            status: 'verified',
            email: resume.email,
            expiresAt: resume.expiresAt,
          });
        }
      } catch (error: any) {
        if (!cancelled) setLoadError(error?.message || 'Dieser Einladungslink ist ungültig.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void loadInvitation();
    return () => { cancelled = true; };
  }, [token]);

  const isUsable = invitation?.status?.toLowerCase() === 'active';
  const formattedExpiry = useMemo(() => {
    if (!invitation?.expiresAt) return '';
    const date = new Date(invitation.expiresAt);
    return Number.isNaN(date.getTime()) ? invitation.expiresAt : new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' }).format(date);
  }, [invitation?.expiresAt]);

  const finish = (auth: any) => {
    persistAuth(auth);
    clearEmailVerificationResume();
    navigate(
      buildAvatarShareLiveTarget(invitation?.customerSlug, invitation?.agentSlug),
      { replace: true },
    );
  };

  const completeRegistration = async (verificationId: string) => {
    if (registrationStartedRef.current) return;
    registrationStartedRef.current = true;
    setFormError('');
    setFormNotice('');
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/avatar-shares/${encodeURIComponent(token)}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          email: email.trim(),
          password,
          emailVerificationId: verificationId,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (response.status === 409 && payload?.code === 'account_exists') {
        registrationStartedRef.current = false;
        setEmailVerification(null);
        clearEmailVerificationResume();
        setIdentifier(payload.identifier || email.trim());
        setPassword('');
        setMode('login');
        setFormNotice(
          'Für diese E-Mail-Adresse besteht bereits ein Konto. Melden Sie sich an; anschließend wird der Avatar direkt freigeschaltet.',
        );
        return;
      }
      if (!response.ok) throw new Error(payload?.error || 'Konto konnte nicht erstellt werden.');
      finish(payload.auth);
    } catch (error: any) {
      registrationStartedRef.current = false;
      setFormError(error?.message || 'Konto konnte nicht erstellt werden.');
    } finally { setIsSubmitting(false); }
  };

  const requestEmailVerification = async () => {
    setFormError('');
    setFormNotice('');
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/avatar-shares/${encodeURIComponent(token)}/email-verification/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.challengeId) throw new Error(payload?.error || 'Bestätigungs-E-Mail konnte nicht gesendet werden.');
      setEmailVerification({
        challengeId: payload.challengeId,
        status: payload.status || 'pending',
        email: payload.email || email.trim(),
        expiresAt: payload.expiresAt,
      });
    } catch (error: any) {
      setFormError(error?.message || 'Bestätigungs-E-Mail konnte nicht gesendet werden.');
    } finally { setIsSubmitting(false); }
  };

  const handleRegister = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (emailVerification?.status === 'verified') {
      await completeRegistration(emailVerification.challengeId);
      return;
    }
    await requestEmailVerification();
  };

  useEffect(() => {
    if (!emailVerification || emailVerification.status !== 'pending') return;
    let cancelled = false;
    let inFlight = false;
    const checkStatus = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await fetch(`${API_BASE}/api/customer/workspace/setup/email-verification/status/${encodeURIComponent(emailVerification.challengeId)}`);
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.status) throw new Error(payload?.error || 'Bestätigungsstatus konnte nicht geprüft werden.');
        if (cancelled) return;
        const nextStatus = String(payload.status);
        setEmailVerification((current) => current && current.challengeId === emailVerification.challengeId
          ? { ...current, status: nextStatus, expiresAt: payload.expiresAt || current.expiresAt }
          : current);
        if (nextStatus === 'verified') {
          await completeRegistration(emailVerification.challengeId);
        } else if (nextStatus === 'expired' || nextStatus === 'consumed') {
          setFormError('Der Bestätigungslink ist nicht mehr gültig. Bitte fordern Sie einen neuen an.');
        }
      } catch (error: any) {
        if (!cancelled) setFormError(error?.message || 'Bestätigungsstatus konnte nicht geprüft werden.');
      } finally { inFlight = false; }
    };
    const interval = window.setInterval(() => { void checkStatus(); }, 2500);
    void checkStatus();
    return () => { cancelled = true; window.clearInterval(interval); };
    // Registration fields deliberately stay in memory while the inbox is checked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerification?.challengeId, emailVerification?.status]);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      let loginResponse = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      });
      let loginPayload = await loginResponse.json().catch(() => null);
      if (!loginResponse.ok) {
        const adminLoginResponse = await fetch(`${API_BASE}/api/admin/login`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: identifier.trim(), password }),
        });
        const adminLoginPayload = await adminLoginResponse.json().catch(() => null);
        if (adminLoginResponse.ok) {
          loginResponse = adminLoginResponse;
          loginPayload = adminLoginPayload;
        }
      }
      if (!loginResponse.ok) throw new Error(loginPayload?.error || 'Anmeldung fehlgeschlagen.');

      const isSuperadmin = loginPayload?.role === 'superadmin'
        || loginPayload?.platform_role === 'superadmin'
        || loginPayload?.platform_role === 'owner';
      if (isSuperadmin) {
        // Provider admins already have global avatar access. The legacy admin
        // identity deliberately has no app_auth.users row, so it cannot and
        // does not need to consume a customer invitation or create a grant.
        finish(loginPayload);
        return;
      }

      const acceptResponse = await fetch(`${API_BASE}/api/avatar-shares/${encodeURIComponent(token)}/accept`, {
        method: 'POST', headers: { Authorization: `Bearer ${loginPayload.token}` },
      });
      const acceptPayload = await acceptResponse.json().catch(() => null);
      if (!acceptResponse.ok) throw new Error(acceptPayload?.error || 'Einladung konnte nicht angenommen werden.');
      finish(acceptPayload.auth || loginPayload);
    } catch (error: any) {
      setFormError(error?.message || 'Anmeldung fehlgeschlagen.');
    } finally { setIsSubmitting(false); }
  };

  const pageClasses = isDarkMode ? 'bg-slate-950 text-slate-50' : 'bg-slate-100 text-slate-950';
  const cardClasses = isDarkMode ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white';
  const panelClasses = isDarkMode ? 'border-white/10 bg-slate-950/60' : 'border-slate-200 bg-slate-50';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-600';
  const inputClasses = isDarkMode ? 'border-slate-700 bg-slate-950 text-white focus:border-blue-400' : 'border-slate-300 bg-white text-slate-950 focus:border-blue-500';

  return (
    <main className={`min-h-screen px-4 py-8 sm:px-6 ${pageClasses}`}>
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl flex-col justify-center">
        {isLoading ? <div role="status" className={`flex items-center justify-center gap-3 rounded-lg border p-8 ${cardClasses}`}><Loader2 className="h-5 w-5 animate-spin text-blue-500" /> Einladung wird geprüft…</div> : null}

        {!isLoading && (loadError || !invitation || !isUsable) ? (
          <section className={`rounded-lg border p-6 shadow-sm sm:p-8 ${cardClasses}`} role="alert">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300"><Lock className="h-5 w-5" /></div>
            <p className={`mt-6 text-xs font-bold uppercase tracking-[0.22em] ${mutedClasses}`}>Zugang nicht verfügbar</p>
            <h1 className="mt-2 text-2xl font-semibold">Einladung kann nicht verwendet werden</h1>
            <p className={`mt-3 text-sm leading-6 ${mutedClasses}`}>{loadError || getInvitationError(invitation?.status || 'invalid')}</p>
            <p className={`mt-6 border-t pt-4 text-sm ${isDarkMode ? 'border-white/10' : 'border-slate-200'} ${mutedClasses}`}>Bitte wenden Sie sich an die Person, die Ihnen den Link gesendet hat.</p>
          </section>
        ) : null}

        {!isLoading && invitation && isUsable ? (
          <section className={`overflow-hidden rounded-lg border shadow-[0_24px_70px_rgba(15,23,42,0.12)] ${cardClasses}`}>
            <header className="border-b border-blue-200 bg-blue-50 px-5 py-5 dark:border-blue-400/20 dark:bg-blue-950/35 sm:px-8">
              <div className="flex items-start gap-4"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-700 text-white"><KeyRound className="h-5 w-5" /></div><div><p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700 dark:text-blue-200">Persönliche Einladung</p><h1 className="mt-1 text-2xl font-semibold">Zugang zu {invitation.agentName}</h1><p className={`mt-1 text-sm ${mutedClasses}`}>Eingeladen von {invitation.customerName}</p></div></div>
            </header>

            <div className="space-y-6 p-5 sm:p-8">
              <div className={`flex flex-col items-start gap-2 rounded-lg border px-3 py-3 sm:grid sm:grid-cols-[auto_1fr_auto_1fr_auto] sm:items-center ${panelClasses}`} aria-label="Zugriffsbereich">
                <span className="break-words text-xs font-semibold">{invitation.customerName}</span><span className="hidden h-px bg-blue-500/40 sm:block" /><span className="break-words text-xs font-semibold text-blue-700 dark:text-blue-200">{invitation.agentName}</span><span className="hidden h-px bg-blue-500/40 sm:block" /><span className="inline-flex items-center gap-1 text-xs font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Nur dieser Avatar</span>
              </div>

              <div>
                <div className={`grid grid-cols-2 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`} role="tablist" aria-label="Zugangsart">
                  <button type="button" role="tab" aria-selected={mode === 'register'} onClick={() => { setMode('register'); setFormError(''); setFormNotice(''); setPassword(''); }} className={`border-b-2 px-3 py-3 text-sm font-semibold transition ${mode === 'register' ? 'border-blue-600 text-blue-700 dark:text-blue-200' : `border-transparent ${mutedClasses}`}`}><UserPlus className="mr-2 inline h-4 w-4" />Konto erstellen</button>
                  <button type="button" role="tab" aria-selected={mode === 'login'} onClick={() => { setMode('login'); setFormError(''); setFormNotice(''); setIdentifier(email.trim()); setPassword(''); }} className={`border-b-2 px-3 py-3 text-sm font-semibold transition ${mode === 'login' ? 'border-blue-600 text-blue-700 dark:text-blue-200' : `border-transparent ${mutedClasses}`}`}><Lock className="mr-2 inline h-4 w-4" />Bereits registriert</button>
                </div>

                {mode === 'register' ? (
                  <form onSubmit={handleRegister} className="mt-5 space-y-4">
                    <label className="block space-y-1.5"><span className="text-sm font-semibold">Benutzername</span><input required disabled={emailVerification?.status === 'pending'} autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none disabled:cursor-wait disabled:opacity-70 ${inputClasses}`} /></label>
                    <label className="block space-y-1.5"><span className="text-sm font-semibold">E-Mail-Adresse</span><input required disabled={emailVerification?.status === 'pending'} type="email" autoComplete="email" readOnly={Boolean(invitation.recipientEmail)} value={email} onChange={(event) => { setEmail(event.target.value); setEmailVerification(null); registrationStartedRef.current = false; }} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none read-only:cursor-not-allowed read-only:opacity-75 disabled:cursor-wait disabled:opacity-70 ${inputClasses}`} />{invitation.recipientEmail ? <span className={`flex items-center gap-1.5 text-xs ${mutedClasses}`}><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />Diese Einladung ist an diese E-Mail-Adresse gebunden.</span> : null}</label>
                    <label className="block space-y-1.5"><span className="text-sm font-semibold">Passwort</span><input required disabled={emailVerification?.status === 'pending'} minLength={8} type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none disabled:cursor-wait disabled:opacity-70 ${inputClasses}`} /><span className={`text-xs ${mutedClasses}`}>Mindestens 8 Zeichen.</span></label>
                    {emailVerification?.status === 'pending' ? (
                      <div className="border-l-4 border-blue-500 bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950/35 dark:text-blue-100" role="status">
                        <p className="font-semibold">Bestätigungslink gesendet</p>
                        <p className="mt-1 leading-6">Öffnen Sie die E-Mail an {emailVerification.email} und bestätigen Sie dort Ihre Adresse. Diese Seite wartet automatisch.</p>
                        <button type="button" onClick={() => void requestEmailVerification()} disabled={isSubmitting} className="mt-3 text-xs font-semibold underline underline-offset-4 disabled:opacity-50">E-Mail erneut senden</button>
                      </div>
                    ) : null}
                    {emailVerification?.status === 'verified' ? <p role="status" className="border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100">E-Mail-Adresse bestätigt. Das Konto wird erstellt…</p> : null}
                    {formError ? <p role="alert" className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-100">{formError}</p> : null}
                    <button disabled={isSubmitting || emailVerification?.status === 'pending'} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:opacity-60">{isSubmitting || emailVerification?.status === 'pending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} {emailVerification ? 'Registrierung abschließen' : 'E-Mail bestätigen und fortfahren'}</button>
                  </form>
                ) : (
                  <form onSubmit={handleLogin} className="mt-5 space-y-4">
                    {formNotice ? <p role="status" className="border-l-4 border-blue-500 bg-blue-50 p-3 text-sm leading-6 text-blue-900 dark:bg-blue-950/35 dark:text-blue-100">{formNotice}</p> : null}
                    <label className="block space-y-1.5"><span className="text-sm font-semibold">E-Mail oder Benutzername</span><input required autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none ${inputClasses}`} /></label>
                    <label className="block space-y-1.5"><span className="text-sm font-semibold">Passwort</span><input required type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none ${inputClasses}`} /></label>
                    {formError ? <p role="alert" className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-100">{formError}</p> : null}
                    <button disabled={isSubmitting} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:opacity-60">{isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Anmelden und Einladung annehmen</button>
                  </form>
                )}
              </div>

              <footer className={`border-t pt-4 text-xs leading-5 ${isDarkMode ? 'border-white/10' : 'border-slate-200'} ${mutedClasses}`}>
                Ihr Konto erhält ausschließlich Zugriff auf <strong className="text-current">{invitation.agentName}</strong>.{formattedExpiry ? ` Die Einladung ist bis ${formattedExpiry} gültig.` : ''}
              </footer>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
};

export default AvatarSharePage;
